import { TenantUserService } from "@/services/APIs";
import { post } from "@/services/axiosUitils";

const EMPTY_LOCATION = {
  latitude: "",
  longitude: "",
  place_id: "",
  name: "",
  display_name: "",
  country: "",
  state: "",
  district: "",
  city: "",
  street: "",
  postal_code: "",
  phone: "",
};

/** Default password for admin-provisioned school accounts — meets backend policy. */
export const SCHOOL_DEFAULT_PASSWORD = "Karuna@123";

export const normalizeSchoolMobile = (mobileNumber: string): string =>
  String(mobileNumber || "")
    .replace(/\D/g, "")
    .slice(0, 10);

export function getKarunaPhoneCode(): string {
  const trimmed = (import.meta.env.VITE_OTP_PHONE_CODE ?? "")
    .replace(/^"|"$/g, "")
    .trim();
  return trimmed !== "" ? trimmed : "73835e38-252c-4232-9b5d-474efc1fb2f4";
}

const PLACEHOLDER_EMAIL_VALUES = new Set([
  "n/a",
  "na",
  "none",
  "null",
  "undefined",
  "-",
  "--",
  "nil",
  "not available",
]);

export function isValidSchoolEmail(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  if (PLACEHOLDER_EMAIL_VALUES.has(trimmed.toLowerCase())) {
    return false;
  }
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

export function resolveSchoolLoginEmail(
  email: string,
  phoneNumber: string
): string {
  const normalizedPhone = normalizeSchoolMobile(phoneNumber);
  if (isValidSchoolEmail(email)) {
    return email.trim().toLowerCase();
  }
  // Must be a valid TLD — Django rejects `.local` emails with 400.
  return `${normalizedPhone}@school.karuna.org`;
}

function splitSchoolNameForAuth(schoolName: string): {
  firstName: string;
  lastName: string;
} {
  const trimmed = schoolName.trim() || "School";
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return { firstName: parts[0] ?? "School", lastName: "School" };
  }
  return {
    firstName: parts[0] ?? "School",
    lastName: parts.slice(1).join(" ") || "School",
  };
}

export function buildSchoolTenantUserPayload(options: {
  email: string;
  phoneNumber: string;
  schoolName: string;
  password?: string;
}): Record<string, unknown> {
  const normalizedPhone = normalizeSchoolMobile(options.phoneNumber);
  const schoolName = options.schoolName.trim() || "School";
  const email = resolveSchoolLoginEmail(options.email, normalizedPhone);

  return {
    email,
    password: options.password ?? SCHOOL_DEFAULT_PASSWORD,
    phone_number: normalizedPhone,
    mobile_number: normalizedPhone,
    first_name: schoolName,
    last_name: "",
    full_name: schoolName,
    username: email.split("@")[0] || normalizedPhone,
    user_type: "school",
    profile_picture: "",
    school_name: schoolName,
    location: {
      ...EMPTY_LOCATION,
      phone: normalizedPhone,
    },
  };
}

export function buildSchoolAuthSignupPayload(options: {
  email: string;
  phoneNumber: string;
  schoolName: string;
  password?: string;
}): Record<string, unknown> {
  const normalizedPhone = normalizeSchoolMobile(options.phoneNumber);
  const email = resolveSchoolLoginEmail(options.email, normalizedPhone);
  const { firstName, lastName } = splitSchoolNameForAuth(options.schoolName);

  // Backend requires phone_code whenever phone_number is sent on POST /api/auth/.
  return {
    email,
    password: options.password ?? SCHOOL_DEFAULT_PASSWORD,
    first_name: firstName,
    last_name: lastName,
    username: normalizedPhone,
    phone_number: normalizedPhone,
    phone_code: getKarunaPhoneCode(),
  };
}

/** Read school fields from flat or nested Formily values (basicGrid, basicInfo, etc.). */
export function extractSchoolFormFields(formValues: Record<string, unknown>): {
  schoolName: string;
  contactNumber: string;
  email: string;
  kcNo: string;
} {
  const sources: Record<string, unknown>[] = [];

  const addSource = (obj: unknown) => {
    if (obj && typeof obj === "object" && !Array.isArray(obj)) {
      sources.push(obj as Record<string, unknown>);
    }
  };

  addSource(formValues);
  addSource(formValues.basicGrid);
  addSource(formValues.basicInfo);
  addSource(formValues.basic_info);
  addSource(formValues.basic_grid);

  const pick = (...keys: string[]): string => {
    for (const src of sources) {
      for (const key of keys) {
        const value = src[key];
        if (
          value !== undefined &&
          value !== null &&
          String(value).trim() !== ""
        ) {
          return String(value).trim();
        }
      }
    }
    return "";
  };

  const rawEmail = pick("schoolemail", "school_email", "email", "emailid");

  return {
    schoolName: pick("schoolname", "school_name"),
    contactNumber: pick(
      "contact",
      "contact_number",
      "schoolcontact",
      "school_contact"
    ),
    email: isValidSchoolEmail(rawEmail) ? rawEmail.trim().toLowerCase() : "",
    kcNo: pick("kcno", "kc_no"),
  };
}

export function extractTenantUsersList(response: unknown): any[] {
  const data = response as { users?: unknown; results?: unknown } | null;
  const usersList = data?.users ?? data?.results ?? [];
  return Array.isArray(usersList) ? usersList : [];
}

function extractErrorMessage(error: unknown): string {
  const err = error as {
    response?: { data?: Record<string, unknown> };
    message?: string;
  };
  const data = err?.response?.data;
  if (data && typeof data === "object") {
    const fieldMessages = Object.entries(data).flatMap(([key, value]) => {
      if (Array.isArray(value)) {
        return value.map(item => `${key}: ${String(item)}`);
      }
      if (typeof value === "string") {
        return [`${key}: ${value}`];
      }
      return [];
    });
    if (fieldMessages.length > 0) {
      return fieldMessages.join(". ");
    }
    if (typeof data.detail === "string") {
      return data.detail;
    }
    if (typeof data.message === "string") {
      return data.message;
    }
    const nested = data.error as { message?: string } | undefined;
    if (typeof nested?.message === "string") {
      return nested.message;
    }
  }
  return err?.message ?? "";
}

function isDuplicateAuthError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes("already exists") ||
    lower.includes("already registered") ||
    lower.includes("duplicate") ||
    lower.includes("unique") ||
    lower.includes("phone number already exists") ||
    lower.includes("phone_number_exists") ||
    lower.includes("user already exists") ||
    lower.includes("already been taken")
  );
}

function isAuthUserAlreadyProvisionedError(error: unknown): boolean {
  if (isDuplicateAuthError(extractErrorMessage(error))) {
    return true;
  }

  const err = error as {
    response?: { status?: number; data?: unknown };
  };
  if (err.response?.status !== 400) {
    return false;
  }

  const serialized = JSON.stringify(err.response.data ?? {}).toLowerCase();
  return (
    serialized.includes("already") ||
    serialized.includes("unique") ||
    serialized.includes("exists") ||
    serialized.includes("registered")
  );
}

export async function findTenantUserByPhoneOrEmail(
  phoneNumber: string,
  email?: string
): Promise<any | null> {
  try {
    const usersResponse = await TenantUserService.listTenantUsers(1, 1000);
    const usersList = extractTenantUsersList(usersResponse);
    const normalizedPhone = normalizeSchoolMobile(phoneNumber);
    const trimmedEmail = email?.trim();
    const resolvedEmail =
      trimmedEmail ??
      (normalizedPhone ? resolveSchoolLoginEmail("", normalizedPhone) : "");

    if (normalizedPhone) {
      const byPhone = usersList.find((user: any) => {
        const userPhone = normalizeSchoolMobile(user.phone_number ?? "");
        return userPhone === normalizedPhone && userPhone.length > 0;
      });
      if (byPhone) {
        return byPhone;
      }
    }

    if (resolvedEmail) {
      const byEmail = usersList.find(
        (user: any) => user.email?.toLowerCase() === resolvedEmail.toLowerCase()
      );
      if (byEmail) {
        return byEmail;
      }
    }

    return null;
  } catch {
    return null;
  }
}

/** Register school in Django auth (`/api/auth/`) so OTP login works without signup. */
export async function syncSchoolAuthUser(options: {
  email: string;
  phoneNumber: string;
  schoolName: string;
}): Promise<{
  ok: boolean;
  action: "created" | "skipped" | "failed";
}> {
  const normalizedPhone = normalizeSchoolMobile(options.phoneNumber);
  if (normalizedPhone.length !== 10) {
    return { ok: true, action: "skipped" };
  }

  const payload = buildSchoolAuthSignupPayload(options);

  try {
    await post("/api/auth/", payload, { silent: true });
    return { ok: true, action: "created" };
  } catch (error: unknown) {
    if (isAuthUserAlreadyProvisionedError(error)) {
      // Auth account exists — ensure /api/users/ has the phone linked for login.
      await syncSchoolTenantUser(options);
      return { ok: true, action: "skipped" };
    }

    console.warn(
      "School auth user registration failed:",
      extractErrorMessage(error),
      payload
    );
    return { ok: false, action: "failed" };
  }
}

export async function syncSchoolTenantUser(options: {
  email: string;
  phoneNumber: string;
  schoolName: string;
  oldPhoneNumber?: string;
}): Promise<{
  ok: boolean;
  action: "created" | "updated" | "skipped" | "failed";
}> {
  const normalizedPhone = normalizeSchoolMobile(options.phoneNumber);
  if (normalizedPhone.length !== 10) {
    return { ok: true, action: "skipped" };
  }

  const email = resolveSchoolLoginEmail(options.email, normalizedPhone);
  const schoolName = options.schoolName.trim();

  try {
    let existingUser: any | null = null;

    if (options.oldPhoneNumber) {
      existingUser = await findTenantUserByPhoneOrEmail(
        options.oldPhoneNumber,
        email
      );
    }

    existingUser ??= await findTenantUserByPhoneOrEmail(normalizedPhone, email);

    if (existingUser?.id) {
      // Keep the default password (Karuna@123) on the payload — the backend
      // requires `password` on user updates and rejects with "Password is
      // required." when it is omitted.
      const updatePayload: Record<string, unknown> = {
        ...buildSchoolTenantUserPayload({
          email,
          phoneNumber: normalizedPhone,
          schoolName: schoolName || "School",
          password: SCHOOL_DEFAULT_PASSWORD,
        }),
      };

      await TenantUserService.updateUser(
        existingUser.id,
        updatePayload as any,
        {
          silent: true,
        }
      );
      return { ok: true, action: "updated" };
    }

    const createPayload = buildSchoolTenantUserPayload({
      email,
      phoneNumber: normalizedPhone,
      schoolName: schoolName || "School",
    });

    await TenantUserService.createUser(createPayload as any, { silent: true });
    return { ok: true, action: "created" };
  } catch (error: unknown) {
    const errorMessage = extractErrorMessage(error).toLowerCase();

    if (isDuplicateAuthError(errorMessage)) {
      return { ok: true, action: "skipped" };
    }

    console.warn("School tenant user sync failed:", error);
    return { ok: false, action: "failed" };
  }
}

/** Provision both auth login (`/api/auth/`) and tenant user (`/api/users/`). */
export async function provisionSchoolLoginAccount(options: {
  email: string;
  phoneNumber: string;
  schoolName: string;
  oldPhoneNumber?: string;
}): Promise<{
  auth: { ok: boolean; action: "created" | "skipped" | "failed" };
  tenant: {
    ok: boolean;
    action: "created" | "updated" | "skipped" | "failed";
  };
}> {
  const tenant = await syncSchoolTenantUser(options);
  const auth = await syncSchoolAuthUser(options);
  return { auth, tenant };
}
